import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen">
      {/* Left: Form */}
      <div className="flex flex-col justify-center w-full max-w-md px-8 py-12 bg-white">
        <div className="mb-10">
          <img src="/logo.svg" alt="Relay" className="h-28 w-auto" />
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-semibold font-heading mb-1.5" style={{ color: "#0F1419" }}>Welcome back</h1>
          <p className="text-slate-500 text-sm">Sign in to your account to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="login-error">
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
              required
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: "#0F1419" }}>Password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input pr-10"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="toggle-password-visibility"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full justify-center py-2.5"
            data-testid="login-submit-button"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Signing in...
              </span>
            ) : "Sign in"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-slate-500 space-y-2">
          <p>
            <Link to="/forgot-password" className="font-medium hover:underline" style={{ color: "#FF5C35" }}>
              Forgot your password?
            </Link>
          </p>
          <p>
            Don't have an account?{" "}
            <Link to="/register" className="font-medium hover:underline" style={{ color: "#FF5C35" }} data-testid="register-link">
              Create account
            </Link>
          </p>
        </div>
      </div>

      {/* Right: Image */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1756435292384-1bf32eff7baf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwaG91c2V8ZW58MHx8fHwxNzc1MzM5MTM0fDA&ixlib=rb-4.1.0&q=85"
          alt="Modern real estate"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom right, rgba(15,20,25,0.75), rgba(255,92,53,0.25))" }} />
        <div className="absolute bottom-10 left-10 right-10">
          <blockquote className="text-white text-xl font-medium font-heading leading-snug">
            "The SMS tool that finally makes follow-up effortless for real estate teams."
          </blockquote>
          <p className="text-sm mt-2" style={{ color: "#FFF0EB" }}>Powered by FollowUp Boss</p>
        </div>
      </div>
    </div>
  );
}
